import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { useAuthStore } from '@/auth/auth-store';
import type {
  GetInvoicesQuery,
  InvoiceMainModel,
  PagedResults,
} from '@/shared/types/billing';

/**
 * Patient invoices list. Powers the Invoices sub-tab of Patient Billing
 * (PROD-733).
 *
 * Wire (verified against swagger 2026-05-15 +
 * Flutter `patient_billing_data_source_impl.dart:62`):
 *
 *   POST /Billing/Invoice/SearchInvoices/{patientId}
 *        body: GetInvoicesQuery
 *        → PagedResults<InvoiceMainModel>
 *
 * Note: swagger declares this response as `InvoiceDto[]` (a flat array),
 * but the LIVE dev API ships the `PagedResults` envelope with
 * `totalNumberOfPages` / `totalNumberOfRecords` populated. Verified
 * against an empty-list response on 2026-05-15. Trust the wire, not the
 * spec — same root-cause class as the PR #103 PascalCase quirks.
 *
 * `userId` / `organizationId` are pulled from the auth store and shipped
 * in BOTH the headers (api-client) and the body, because the .NET filter
 * pulls from whichever is present. Sending them in the body too is
 * defensive — matches Flutter exactly.
 */

export interface UsePatientInvoicesParams {
  patientId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
  searchQuery?: string;
  isArchived?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  sponsorType?: string[] | null;
  patientPaymentStatus?: string[] | null;
}

export function usePatientInvoices(params: UsePatientInvoicesParams) {
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const userId = useAuthStore((s) => s.user?.userId) ?? '';

  const body: GetInvoicesQuery = {
    searchQuery: params.searchQuery ?? null,
    isArchived: params.isArchived ?? false,
    pageSize: params.pageSize ?? 20,
    pageNumber: params.pageNumber ?? 1,
    userId,
    patientId: params.patientId ?? null,
    startDate: params.startDate ?? null,
    endDate: params.endDate ?? null,
    sponsorType: params.sponsorType ?? null,
    patientPaymentStatus: params.patientPaymentStatus ?? null,
    query: null,
    organizationId: orgId,
  };

  return useQuery({
    queryKey: [
      'patient-invoices',
      params.patientId,
      body.pageNumber,
      body.pageSize,
      body.searchQuery,
      body.isArchived,
      body.startDate,
      body.endDate,
      body.sponsorType,
      body.patientPaymentStatus,
    ],
    queryFn: ({ signal }) =>
      post<PagedResults<InvoiceMainModel>>(
        `${BILLING_BASE}/Invoice/SearchInvoices/${params.patientId}`,
        body,
        { signal },
      ),
    enabled: !!params.patientId,
    placeholderData: (prev) => prev,
  });
}
